import { makeAutoObservable, runInAction } from "mobx";
import Parse from "parse";
import type {
  ScheduleItem,
  ScheduleItemType,
  ScheduleLocation,
} from "../FestivalCalcPage/types";

function mapScheduleItem(obj: Parse.Object): ScheduleItem {
  return {
    objectId: obj.id,
    type: (obj.get("type") ?? "general") as ScheduleItemType,
    title: obj.get("title") ?? "",
    startTime: obj.get("startTime") ?? new Date(),
    durationMinutes: obj.get("durationMinutes") ?? 60,
    locationId: obj.get("locationId") ?? "",
    description: obj.get("description") ?? "",
    capacity: obj.get("capacity") ?? null,
    createdBy: obj.get("createdBy") ?? null,
    _parseItem: obj,
  };
}

export class ScheduleStore {
  items: ScheduleItem[] = [];
  locations: ScheduleLocation[] = [];
  selectedItem: ScheduleItem | null = null;
  loading = true;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async loadAll() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      const [itemResults, configResult] = await Promise.all([
        new Parse.Query("SCH_ScheduleItem").ascending("startTime").find(),
        new Parse.Query("SCH_FestivalConfig").first(),
      ]);
      runInAction(() => {
        this.items = itemResults.map(mapScheduleItem);
        this.locations = configResult?.get("locations") ?? [];
        this.loading = false;
      });
    } catch (e: any) {
      runInAction(() => {
        this.error = e?.message ?? "Fehler beim Laden";
        this.loading = false;
      });
    }
  }

  selectItem(item: ScheduleItem | null) {
    this.selectedItem = item;
  }

  async addItem(data: Omit<ScheduleItem, "objectId" | "createdBy">) {
    const currentUser = Parse.User.current();
    const obj = new Parse.Object("SCH_ScheduleItem");
    obj.set("type", data.type);
    obj.set("title", data.title);
    obj.set("startTime", data.startTime);
    obj.set("durationMinutes", data.durationMinutes);
    obj.set("locationId", data.locationId);
    obj.set("description", data.description ?? "");
    if (data.capacity != null) obj.set("capacity", data.capacity);
    if (currentUser) obj.set("createdBy", currentUser);
    const saved = await obj.save();
    runInAction(() => {
      this.items.push(mapScheduleItem(saved));
      this.items.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    });
  }

  async updateItem(objectId: string, patch: Partial<ScheduleItem>) {
    runInAction(() => {
      const idx = this.items.findIndex((i) => i.objectId === objectId);
      if (idx !== -1) this.items[idx] = { ...this.items[idx], ...patch };
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_ScheduleItem",
      objectId,
    });
    for (const [key, val] of Object.entries(patch)) {
      if (key !== "objectId" && key !== "createdBy") obj.set(key, val);
    }
    await obj.save();
  }

  async deleteItem(objectId: string) {
    runInAction(() => {
      this.items = this.items.filter((i) => i.objectId !== objectId);
    });
    const obj = Parse.Object.fromJSON({
      className: "SCH_ScheduleItem",
      objectId,
    });
    await obj.destroy();
  }
}
